import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTrialDays } from "@/hooks/use-trial-days";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { useSubscription } from "@/hooks/use-subscription";
import { useTrialCheckoutConfirmation } from "@/hooks/use-trial-checkout-confirmation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Printer, Check, Mail, LogOut } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, CURRENCY_SYMBOLS, type Currency } from "@/lib/onboarding-defaults";
import { PrintTipsDialog } from "@/components/PrintTipsDialog";
import { StaffAccessPanel } from "@/components/StaffAccessPanel";
import { ReferralPanel } from "@/components/ReferralPanel";
import { OverageSettingsPanel } from "@/components/OverageSettingsPanel";
import { BuyCreditPackButton } from "@/components/BuyCreditPackButton";
import { SendFeedbackDialog } from "@/components/SendFeedbackDialog";
import { BillingHistory } from "@/components/BillingHistory";
import { DataExportPanel } from "@/components/DataExportPanel";
import { ManagePlanDialog } from "@/components/ManagePlanDialog";
import { GrowthSeatsPanel } from "@/components/GrowthSeatsPanel";
import { RegenerateScanTokenDialog } from "@/components/RegenerateScanTokenDialog";

import { useLocalizedPrices } from "@/hooks/use-localized-prices";
import { monthlyEquivalent } from "@/lib/stripe-prices";
import { validateBookingUrl } from "@/lib/booking-url";
import { withAuth } from "@/lib/server-fn-auth";
import { notifyBookingUrlPendingFn } from "@/lib/admin-notify.functions";

import type { Tables } from "@/integrations/supabase/types";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

type Merchant = Tables<"merchants">;

function SettingsPage() {
  const { t } = useTranslation();
  const { trialDays: displayTrialDays, firstChargeDay } = useTrialDays();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { current } = useCurrentLocation();
  const { subscription, loading: subLoading, isActive, tier, isCanceling } = useSubscription();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const { beginCheckout, confirmationDialog, checkoutLoading, hasEverSubscribed } = useTrialCheckoutConfirmation({
    userEmail: user?.email,
    userId: user?.id,
  });

  // Days remaining on the in-app free trial. Surfaced to the user so they can
  // decide whether to wait or pick a plan now (picking a plan starts a fresh
  // Stripe trial and ends the in-app trial — see notice below).
  const trialDaysLeft = (() => {
    if (!merchant?.trial_until) return 0;
    const ms = new Date(merchant.trial_until).getTime() - Date.now();
    return ms > 0 ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : 0;
  })();
  const [businessName, setBusinessName] = useState("");
  const [slug, setSlug] = useState("");
  const [avgOrderValue, setAvgOrderValue] = useState(500);
  const [currency, setCurrency] = useState<string>("SEK");
  const [bookingUrl, setBookingUrl] = useState<string>("");
  const [bookingUrlError, setBookingUrlError] = useState<string | null>(null);
  const [includeBookingUrl, setIncludeBookingUrl] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [showPrintTips, setShowPrintTips] = useState(false);
  const [isYearly, setIsYearly] = useState(true);
  const prices = useLocalizedPrices([
    "tack_starter_monthly",
    "tack_starter_yearly",
    "tack_standard_monthly",
    "tack_standard_yearly",
    "tack_growth_monthly",
    "tack_growth_yearly",
    "tack_starter_overage_100",
    "tack_standard_overage_100",
    "tack_growth_overage_100",
  ]);

  const priceLabel = (id: string, fallback: string) => prices?.[id]?.formatted ?? fallback;

  const startPlanCheckout = (priceId: string, planName: string) => {
    beginCheckout({
      priceId,
      planName,
      cadence: isYearly ? "yearly" : "monthly",
    });
  };

  useEffect(() => {
    if (!user || !current?.id) return;
    setLoading(true);
    async function load() {
      const { data } = await supabase
        .from("merchants")
        .select("*")
        .eq("id", current!.id)
        .maybeSingle();
      if (data) {
        setMerchant(data);
        setBusinessName(data.business_name);
        setSlug(data.slug);
        setAvgOrderValue(data.avg_order_value ?? 500);
        setCurrency(data.currency || "SEK");
        const url = (data as any).booking_url ?? "";
        setBookingUrl(url);
        setIncludeBookingUrl(!!url);
      }
      setLoading(false);
    }
    load();
  }, [user, current?.id]);

  const handleSave = async () => {
    if (!merchant) return;
    let bookingUrlValue: string | null = null;
    if (includeBookingUrl) {
      const validation = validateBookingUrl(bookingUrl, { required: true });
      if (!validation.ok) {
        setBookingUrlError(
          validation.errorKey === "empty"
            ? t("settings.business.bookingUrlRequired")
            : t("settings.business.bookingUrlInvalid")
        );
        return;
      }
      setBookingUrl(validation.value);
      bookingUrlValue = validation.value;
    }
    setBookingUrlError(null);
    setSaving(true);
    const { error } = await supabase
      .from("merchants")
      .update({
        business_name: businessName,
        // slug is intentionally immutable: printed QR codes point at /tack/{slug}

        avg_order_value: avgOrderValue,
        currency,
        booking_url: bookingUrlValue,
      } as any)
      .eq("id", merchant.id);

    if (error) {
      toast.error(error.message.includes("duplicate") ? t("settings.business.slugTaken") : error.message);
    } else {
      toast.success(t("settings.business.saved"));
      if (bookingUrlValue) {
        try {
          await withAuth(notifyBookingUrlPendingFn)({ data: { merchantId: merchant.id } });
        } catch (err) {
          console.warn("[settings] booking URL notification failed", err);
        }
      }
      // Refresh so the approval badge reflects auto-approved allowlisted hosts.
      const { data: refreshed } = await supabase
        .from("merchants")
        .select("*")
        .eq("id", merchant.id)
        .maybeSingle();
      if (refreshed) setMerchant(refreshed);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("settings.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {/* Subscription & Billing */}
          <Card id="subscription">
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <CreditCard size={18} /> {t("settings.subscription.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {subLoading ? (
                <p className="text-sm text-muted-foreground">{t("settings.subscription.loading")}</p>
              ) : !isActive ? (
                <div className="space-y-6">
                  {trialDaysLeft > 0 ? (
                    <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                          {t("settings.subscription.currentPlan")}
                        </div>
                        <div className="min-w-0">
                          <p className="font-display font-semibold text-foreground">
                            {t("settings.subscription.trialLeft", { count: trialDaysLeft })}
                          </p>
                          {merchant?.trial_until && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {t("settings.subscription.trialEnds", { date: new Date(merchant.trial_until).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg bg-tack-amber/10 border border-tack-amber/30 px-3 py-2.5 text-xs text-foreground">
                        <p className="font-semibold mb-1">{t("settings.subscription.trialWarnTitle")}</p>
                        <p className="text-muted-foreground">
                          {t("settings.subscription.trialWarnBody", { count: trialDaysLeft })}
                        </p>
                      </div>
                    </div>
                  ) : hasEverSubscribed ? (
                    <div className="rounded-xl border border-tack-amber/40 bg-tack-amber/5 p-4">
                      <p className="text-sm font-medium text-foreground">{t("settings.subscription.noActive")}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("settings.subscription.noActiveReturning")}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("settings.subscription.noActiveNew", { days: displayTrialDays, firstChargeDay })}</p>
                  )}




                  {/* Welcome offer banner */}
                  <div className="rounded-2xl bg-gradient-to-br from-primary/15 via-tack-warm to-primary/10 border border-primary/25 px-6 py-6 md:px-8 md:py-7 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                      <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center">
                        <Check size={26} className="text-primary" />
                      </div>
                      <div className="flex-1 text-center md:text-left">
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                          {t("landing.welcomeOffer.eyebrow")}
                        </div>
                        <h2 className="text-lg md:text-xl font-display font-bold text-foreground leading-snug">
                          {t("landing.welcomeOffer.headline", { days: displayTrialDays })}
                        </h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t("landing.welcomeOffer.subline")}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground/80">
                          {t("landing.welcomeOffer.footnote")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Billing toggle (right above cards) */}
                  <div className="relative flex items-center justify-center gap-3">
                    <span className={`text-sm font-medium ${!isYearly ? "text-foreground" : "text-muted-foreground"}`}>{t("settings.subscription.monthly")}</span>
                    <button
                      onClick={() => setIsYearly(!isYearly)}
                      aria-label="Toggle billing cadence"
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${isYearly ? "bg-primary" : "bg-muted"}`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${isYearly ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                    <span className={`text-sm font-medium ${isYearly ? "text-foreground" : "text-muted-foreground"}`}>{t("settings.subscription.yearly")}</span>
                    <Badge
                      className={`text-xs bg-tack-green/10 text-tack-green border-tack-green/20 whitespace-nowrap transition-opacity ${isYearly ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    >
                      {t("settings.subscription.saveBadge")}
                    </Badge>
                  </div>


                  {/* Unified disclaimers (identical to /pricing) */}
                  <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto -mt-2">
                    {t("landing.availabilityNote")}
                  </p>
                  <p className="text-xs text-muted-foreground text-center mb-4 max-w-2xl mx-auto">
                    {t("landing.taxDisclaimer")}
                  </p>


                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                    {/* Starter */}
                    <Card className="border-border/50 flex flex-col">
                      <CardContent className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-display font-bold text-xl">{t("landing.tiers.starter")}</h3>
                          <Badge className="bg-tack-green/10 text-tack-green border-tack-green/20 text-xs">
                            {t("landing.trialBadgeSolo", { days: displayTrialDays })}
                          </Badge>
                        </div>
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="text-3xl font-display font-bold">
                            {isYearly
                              ? (monthlyEquivalent(prices?.["tack_starter_yearly"]) ?? "166 kr")
                              : priceLabel("tack_starter_monthly", "199 kr")}
                          </span>
                          <span className="text-muted-foreground text-sm">{t("settings.subscription.perMonth")}</span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-muted-foreground">
                            {t("settings.subscription.billedYearly", { price: priceLabel("tack_starter_yearly", "1 990 kr") })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mb-3">{t("settings.subscription.exclVat")}</p>
                        <ul className="space-y-2 flex-1">
                          {[
                            t("settings.subscription.features.starter.qr"),
                            t("settings.subscription.features.starter.sms", { price: priceLabel("tack_starter_overage_100", "150 kr") }),
                            t("settings.subscription.features.starter.send"),
                            t("settings.subscription.features.starter.qrs"),
                            t("settings.subscription.features.starter.dash"),
                            t("settings.subscription.features.starter.burn"),
                          ].map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm">
                              <Check size={14} className="text-tack-green shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <Button
                          variant="outline"
                          className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight"
                          disabled={checkoutLoading}
                          onClick={() => startPlanCheckout(isYearly ? "tack_starter_yearly" : "tack_starter_monthly", t("landing.tiers.starter"))}
                        >
                          {checkoutLoading ? t("settings.subscription.loadingCheckout") : t("settings.subscription.chooseStarter")}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Standard — Popular */}
                    <Card className="border-primary shadow-md relative flex flex-col">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                        {t("landing.popular")}
                      </div>
                      <CardContent className="p-6 flex flex-col flex-1">
                        <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.standard")}</h3>
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="text-3xl font-display font-bold">
                            {isYearly
                              ? (monthlyEquivalent(prices?.["tack_standard_yearly"]) ?? "416 kr")
                              : priceLabel("tack_standard_monthly", "499 kr")}
                          </span>
                          <span className="text-muted-foreground text-sm">{t("settings.subscription.perMonth")}</span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-muted-foreground">
                            {t("settings.subscription.billedYearly", { price: priceLabel("tack_standard_yearly", "4 990 kr") })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mb-3">{t("settings.subscription.exclVat")}</p>
                        <ul className="space-y-2 flex-1">
                          {[
                            t("settings.subscription.features.standard.everything"),
                            t("settings.subscription.features.standard.sms", { price: priceLabel("tack_standard_overage_100", "120 kr") }),
                            t("settings.subscription.features.standard.flash"),
                          ].map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm">
                              <Check size={14} className="text-tack-green shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <Button
                          className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight"
                          disabled={checkoutLoading}
                          onClick={() => startPlanCheckout(isYearly ? "tack_standard_yearly" : "tack_standard_monthly", t("landing.tiers.standard"))}
                        >
                          {checkoutLoading ? t("settings.subscription.loadingCheckout") : t("settings.subscription.chooseStandard")}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Growth */}
                    <Card className="border-border/50 flex flex-col">
                      <CardContent className="p-6 flex flex-col flex-1">
                        <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.growth")}</h3>
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="text-3xl font-display font-bold">
                            {isYearly
                              ? (monthlyEquivalent(prices?.["tack_growth_yearly"]) ?? "999 kr")
                              : priceLabel("tack_growth_monthly", "1 199 kr")}
                          </span>
                          <span className="text-muted-foreground text-sm">{t("settings.subscription.perMonth")}</span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-muted-foreground">
                            {t("settings.subscription.billedYearly", { price: priceLabel("tack_growth_yearly", "11 988 kr") })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mb-3">{t("settings.subscription.exclVat")}</p>
                        <ul className="space-y-2 flex-1">
                          {[
                            t("settings.subscription.features.growth.everything"),
                            t("settings.subscription.features.growth.sms", { price: priceLabel("tack_growth_overage_100", "90 kr") }),
                            t("settings.subscription.features.growth.flash"),
                            t("settings.subscription.features.growth.support"),
                          ].map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm">
                              <Check size={14} className="text-tack-green shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <Button
                          variant="outline"
                          className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight"
                          disabled={checkoutLoading}
                          onClick={() => startPlanCheckout(isYearly ? "tack_growth_yearly" : "tack_growth_monthly", t("landing.tiers.growth"))}
                        >
                          {checkoutLoading ? t("settings.subscription.loadingCheckout") : t("settings.subscription.chooseGrowth")}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Enterprise */}
                    <Card className="border-border/50 flex flex-col">
                      <CardContent className="p-6 flex flex-col flex-1">
                        <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.enterprise")}</h3>
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="text-3xl font-display font-bold">{t("settings.subscription.custom")}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">{t("settings.subscription.talkToUs")}</p>
                        <ul className="space-y-2 flex-1">
                          {[
                            t("settings.subscription.features.enterprise.everything"),
                            t("settings.subscription.features.enterprise.support"),
                            t("settings.subscription.features.enterprise.pricing"),
                          ].map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm">
                              <Check size={14} className="text-tack-green shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <a href="mailto:hello@tack.company?subject=Enterprise%20Plan%20Inquiry" className="block mt-6">
                          <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("settings.subscription.contactUs")}</Button>
                        </a>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge variant={subscription?.status === "active" || subscription?.status === "trialing" ? "default" : "secondary"}>
                      {subscription?.status === "trialing" ? t("settings.subscription.trial") : subscription?.status}
                    </Badge>
                    <span className="font-display font-semibold capitalize">
                      {t("settings.subscription.planLabel", { tier: tier === "starter" ? "Solo" : tier })}
                      {subscription?.price_id?.includes("yearly")
                        ? t("settings.subscription.yearlySuffix")
                        : subscription?.price_id?.includes("monthly")
                          ? t("settings.subscription.monthlySuffix")
                          : ""}
                    </span>
                    {isCanceling && (
                      <Badge variant="secondary" className="text-xs">{t("settings.subscription.cancelsAtPeriodEnd")}</Badge>
                    )}
                  </div>
                  {subscription?.current_period_end && (
                    <p className="text-xs text-muted-foreground">
                      {isCanceling ? t("settings.subscription.accessUntil") : t("settings.subscription.renews")}: {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex gap-3 flex-wrap">
                    <ManagePlanDialog
                      currentTier={tier}
                      isCanceling={isCanceling}
                      isActive={isActive}
                      subscription={subscription}
                      userEmail={user?.email}
                      userId={user?.id}
                    />
                  </div>
                  <BillingHistory
                    environment={subscription?.environment}
                    providerCustomerId={subscription?.provider_customer_id}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Growth extra-location seats */}
          <GrowthSeatsPanel subscription={subscription as any} loading={subLoading} />



          {/* Referral program */}
          <ReferralPanel />

          {/* Auto top-up (overage) — #autotopup is deep-linked from the
              dashboard credits card and the low-credit e-mails. */}
          <div id="autotopup" className="scroll-mt-24">
            <OverageSettingsPanel />
          </div>

          {/* Manual credit pack purchase */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">{t("credits.buyPack")}</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {t("credits.buyPackDescription")}
              </p>
              <BuyCreditPackButton />
            </CardContent>
          </Card>

          {/* Staff access (cashier accounts) */}
          <StaffAccessPanel />

          {/* Business Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">{t("settings.business.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">{t("settings.business.businessName")}</Label>
                <Input id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} maxLength={25} />
                <p className="text-xs text-muted-foreground">{t("settings.business.businessNameHint")}</p>
                {merchant && businessName !== merchant.business_name && (
                  <p className="text-xs text-tack-amber">{t("settings.business.businessNameReprintHint")}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">{t("settings.business.urlSlug")}</Label>
                <Input id="slug" value={slug} readOnly disabled className="bg-muted/40" />
                <p className="text-xs text-muted-foreground">{t("settings.business.feedbackPage")} <code>/tack/{slug || "…"}</code></p>
                <p className="text-xs text-muted-foreground">{t("settings.business.slugLocked")}</p>
              </div>
              <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeBookingUrl}
                    onChange={(e) => {
                      setIncludeBookingUrl(e.target.checked);
                      if (!e.target.checked) setBookingUrlError(null);
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-input accent-primary cursor-pointer"
                  />
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {t("settings.business.bookingUrlToggle")}{" "}
                      <span className="text-primary font-normal text-xs">{t("settings.business.bookingUrlToggleBadge")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("settings.business.bookingUrlToggleHint")}</p>
                  </div>
                </label>
                {includeBookingUrl && (
                  <div className="space-y-1.5 pt-1">
                    <Label htmlFor="bookingUrl" className="text-xs">{t("settings.business.bookingUrl")}</Label>
                    <Input
                      id="bookingUrl"
                      type="url"
                      inputMode="url"
                      value={bookingUrl}
                      onChange={(e) => {
                        setBookingUrl(e.target.value);
                        if (bookingUrlError) setBookingUrlError(null);
                      }}
                      placeholder={t("settings.business.bookingUrlPlaceholder")}
                      aria-invalid={bookingUrlError ? true : undefined}
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.business.bookingUrlHint")}</p>
                    {bookingUrlError && (
                      <p className="text-xs text-destructive">{bookingUrlError}</p>
                    )}
                    {(merchant as any)?.booking_url_status === "pending" && (
                      <p className="text-xs text-tack-amber">{t("settings.business.bookingUrlPending")}</p>
                    )}
                    {(merchant as any)?.booking_url_status === "approved" && (
                      <p className="text-xs text-primary">{t("settings.business.bookingUrlApproved")}</p>
                    )}
                    {(merchant as any)?.booking_url_status === "rejected" && (
                      <p className="text-xs text-destructive">
                        {t("settings.business.bookingUrlRejected")}
                        {(merchant as any)?.booking_url_rejection_reason ? ` — ${(merchant as any).booking_url_rejection_reason}` : ""}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">{t("settings.business.currency")}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c} ({CURRENCY_SYMBOLS[c]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="avgOrderValue">{t("settings.business.avgOrderValue", { symbol: CURRENCY_SYMBOLS[currency as Currency] || currency })}</Label>
                <Input id="avgOrderValue" type="number" min={0} value={avgOrderValue} onChange={(e) => setAvgOrderValue(Number(e.target.value))} />
                <p className="text-xs text-muted-foreground">{t("settings.business.avgOrderValueHint")}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("settings.business.rewardSettingsMoved")}
              </p>

              {(merchant as any)?.sms_optout_code && (
                <div className="space-y-1 rounded-md border border-border/50 bg-muted/30 p-3">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t("settings.business.optoutCodeLabel")}
                  </Label>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-base font-semibold tracking-wider">
                      STOP{(merchant as any).sms_optout_code}
                    </code>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("settings.business.optoutCodeHint")}
                  </p>
                </div>
              )}
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t("settings.business.saving") : t("settings.business.save")}
              </Button>
            </CardContent>
          </Card>

          {/* Printing Tips */}
          <Card className="border-dashed">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("settings.printing.title")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.printing.desc")}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrintTips(true)}
              >
                <Printer size={14} className="mr-1" />
                {t("settings.printing.show")}
              </Button>
            </CardContent>
          </Card>

          <PrintTipsDialog open={showPrintTips} onOpenChange={setShowPrintTips} forceShow />




          {/* Regenerate Scan Token */}
          <Card className="border-dashed">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("settings.scanToken.title")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("settings.scanToken.desc")}
                </p>
              </div>
              {merchant && <RegenerateScanTokenDialog merchant={merchant} onRegenerated={setMerchant} />}
            </CardContent>
          </Card>

          {/* Re-run Onboarding */}
          <Card className="border-dashed">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("settings.onboarding.title")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.onboarding.desc")}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!merchant) return;
                  // Re-runs must NOT clear onboarding_completed — otherwise
                  // exiting midway makes onboarding reappear on every login.
                  navigate({ to: "/onboarding", search: { rerun: true } });
                }}
              >
                {t("settings.onboarding.action")}
              </Button>
            </CardContent>
          </Card>

          <DataExportPanel />


          <Card className="border-dashed">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("settings.signOut.title")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("settings.signOut.desc")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await logout();
                    toast.success(t("settings.signOut.success"));
                    navigate({ to: "/" });
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : t("settings.signOut.failed"));
                  }
                }}
              >
                <LogOut size={14} className="mr-1" />
                {t("settings.signOut.action")}
              </Button>
            </CardContent>
          </Card>

          <div className="pt-2 flex justify-center">
            <SendFeedbackDialog />
          </div>
          {confirmationDialog}
      </div>
    </div>
  );
}
